import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AmountInput, Button, Copy, Row, Segmented, Skeleton } from "../../components/ui";
import { funding } from "../../hl/info";
import { big } from "../../lib/num";
import type { DepositPlan } from "../../sdk";
import { useAccount } from "../../state/account";
import { planDeposit, sendDeposit } from "../../state/actions";
import { shortAddress } from "../../wallet/address";
import { Line, MaxChip } from "./parts";
import { floorUsdc, minimums, typedUsdc, usdcCents, usdcFig, usdcMin, usdcText } from "./shared";

type Source = "spot" | "perp";
type Mode = "standard" | "pooled";

type Step =
  | { at: "form" }
  | { at: "review"; plan: DepositPlan; source: Source; mode: Mode }
  | { at: "done"; plan: DepositPlan; before: bigint | null };

const SOURCE_NAME: Record<Source, string> = { spot: "spot", perp: "perps" };

const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** A deposit from the wallet's Hyperliquid balance: pick, review the sends, sign, wait. */
export function Deposit({ owner }: { owner: string }) {
  const account = useAccount();
  const client = useQueryClient();
  const hl = useQuery({
    queryKey: ["hlFunding", owner],
    queryFn: ({ signal }) => funding(owner, signal),
    refetchInterval: 15_000,
  });
  const [step, setStep] = useState<Step>({ at: "form" });
  const [picked, setPicked] = useState<Source | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  if (step.at === "review") {
    return (
      <Review
        plan={step.plan}
        source={step.source}
        mode={step.mode}
        onBack={() => setStep({ at: "form" })}
        onSent={() => {
          setStep({ at: "done", plan: step.plan, before: account ? big(account.lifetime.deposited) : null });
          setText("");
          void client.invalidateQueries({ queryKey: ["hlFunding"] });
        }}
      />
    );
  }
  if (step.at === "done") return <Done plan={step.plan} before={step.before} onAgain={() => setStep({ at: "form" })} />;

  const data = hl.data;
  const pooled = data?.pooled ?? false;
  const mode: Mode = pooled ? "pooled" : "standard";
  const spot = floorUsdc(data?.spot);
  const perp = floorUsdc(data?.perp);
  // the larger balance until the player picks
  const source: Source = pooled ? "spot" : (picked ?? (perp > spot ? "perp" : "spot"));
  const balance = source === "perp" ? perp : spot;
  const empty = data !== undefined && (pooled ? spot === 0n : spot === 0n && perp === 0n);

  const min = minimums().deposit;
  const amount = typedUsdc(text);
  let reason: string | null = null;
  if (!data) reason = hl.isError ? "Balances unavailable" : "Reading balances";
  else if (amount === null || amount === 0n) reason = "Enter an amount";
  else if (amount < min) reason = `Minimum ${usdcMin(min)}`;
  else if (amount > balance) reason = pooled ? "Not enough on Hyperliquid" : `Not enough in ${SOURCE_NAME[source]}`;

  const review = async () => {
    if (reason !== null || amount === null) return;
    setBusy(true);
    try {
      const plan = await planDeposit(usdcText(amount), source, mode);
      setStep({ at: "review", plan, source, mode });
    } catch {
      // told by a toast
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack-lg">
      {hl.isError && !data ? (
        <div className="note note-warn pf-note-row">
          <span>Could not read your Hyperliquid balances.</span>
          <Button tone="ghost" size="sm" busy={hl.isFetching} onClick={() => void hl.refetch()}>
            Retry
          </Button>
        </div>
      ) : pooled ? (
        <div className="stack-sm">
          <Line label="From">Hyperliquid balance</Line>
          <Row label="Balance">{usdcFig(spot)} USDC</Row>
        </div>
      ) : (
        <div className="field">
          <div className="field-head">
            <span className="label">From Hyperliquid</span>
          </div>
          <Segmented<Source>
            label="Deposit from"
            value={source}
            onChange={(next) => {
              setPicked(next);
              setText("");
            }}
            options={[
              { value: "spot", label: <SourceLabel name="Spot" raw={data ? spot : null} /> },
              { value: "perp", label: <SourceLabel name="Perp" raw={data ? perp : null} /> },
            ]}
          />
        </div>
      )}

      {empty && (
        <p className="note">
          No USDC on Hyperliquid yet. Fund your account at{" "}
          <a href="https://app.hyperliquid.xyz/trade" target="_blank" rel="noreferrer">
            app.hyperliquid.xyz ↗
          </a>{" "}
          first.
        </p>
      )}

      <div className="field">
        <div className="field-head">
          <span className="label">Amount</span>
          <MaxChip
            disabled={!data || balance === 0n}
            onClick={() => {
              if (picked === null) setPicked(source);
              setText(usdcText(balance));
            }}
          />
        </div>
        <AmountInput
          label="Deposit amount"
          value={text}
          onChange={(next) => {
            // typing settles the source, so a refetch never moves it underneath
            if (picked === null && data) setPicked(source);
            setText(next);
          }}
          places={6}
          suffix="USDC"
          onEnter={() => void review()}
        />
        <p className="pf-fine">Minimum {usdcMin(min)}. One deposit at a time.</p>
      </div>

      <Button tone="primary" size="lg" block busy={busy} disabled={reason !== null} onClick={() => void review()}>
        {reason ?? "Review deposit"}
      </Button>
    </div>
  );
}

function SourceLabel({ name, raw }: { name: string; raw: bigint | null }) {
  return (
    <span className="pf-src">
      <span>{name}</span>
      {raw === null ? <Skeleton width={48} height={12} /> : <span className="num pf-src-amount">{usdcCents(raw)}</span>}
    </span>
  );
}

function Review({
  plan,
  source,
  mode,
  onBack,
  onSent,
}: {
  plan: DepositPlan;
  source: Source;
  mode: Mode;
  onBack: () => void;
  onSent: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const amount = big(plan.usdc);
  const fee = big(plan.activationFee);
  const fromPerp = mode === "standard" && source === "perp";
  const signs = plan.sends.length;
  return (
    <div className="stack-lg">
      <div className="pf-review-head">
        <span className="label">You deposit</span>
        <span className="pf-review-figure num">{usdcFig(amount)} USDC</span>
      </div>

      <div className="stack-sm">
        <Line label="From">{mode === "pooled" ? "Hyperliquid balance" : `Hyperliquid ${SOURCE_NAME[source]}`}</Line>
        {fromPerp && plan.opensAccount && <Row label="Moves from perps">{usdcFig(amount + fee)} USDC</Row>}
        <Line label="Deposit address">
          <Copy text={plan.to}>{shortAddress(plan.to)}</Copy>
        </Line>
      </div>

      <ol className="pf-sends" aria-label="What your wallet signs">
        {plan.sends.map((send, index) => (
          <li key={`${send.kind}-${index}`} className="pf-send">
            <span className="pf-send-n num" aria-hidden>
              {index + 1}
            </span>
            <span>{sentence(send.describe)}</span>
          </li>
        ))}
      </ol>

      {plan.opensAccount && (
        <p className="note">
          {usdcMin(fee)} opens the deposit address's HyperCore account. Hyperliquid takes it from your balance on top of the deposit.
        </p>
      )}

      <p className="pf-fine">
        Your wallet switches to Arbitrum to sign {signs > 1 ? `${signs} sends` : "the send"}. No gas.
      </p>

      <div className="pf-actions">
        <Button tone="secondary" size="lg" disabled={busy} onClick={onBack}>
          Back
        </Button>
        <Button
          tone="primary"
          size="lg"
          block
          busy={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await sendDeposit(plan);
              onSent();
            } catch {
              // told by a toast; the plan stays for another try
            } finally {
              setBusy(false);
            }
          }}
        >
          Confirm in wallet
        </Button>
      </div>
    </div>
  );
}

function Done({ plan, before, onAgain }: { plan: DepositPlan; before: bigint | null; onAgain: () => void }) {
  const account = useAccount();
  const arrived = before !== null && account !== null && big(account.lifetime.deposited) > before;
  return (
    <div className="pf-done" role="status">
      <span className={`pf-done-mark ${arrived ? "is-arrived" : ""}`} aria-hidden>
        {arrived ? "✓" : <span className="spinner" />}
      </span>
      <div className="stack-sm pf-done-copy">
        <div className="pf-done-title">{arrived ? "Deposit arrived" : "Deposit sent"}</div>
        <p className="pf-done-body">
          {arrived
            ? `${usdcFig(big(plan.usdc))} USDC is in your bankroll.`
            : "Arrives in your bankroll within a few blocks. Wait for it before sending another."}
        </p>
      </div>
      <Button tone="secondary" onClick={onAgain}>
        New deposit
      </Button>
    </div>
  );
}
